import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
  ChevronDownSmallIcon,
  ChevronLeftIcon,
  GooglePhotosIcon,
  InstagramIcon,
  FacebookIcon,
  FlickrIcon,
  UserIcon,
  Button,
  PopOut,
  PictureIcon,
  Horizontal,
  Vertical,
  ThemeName,
} from '@taopix/taopix-design-system';
import AssetServiceNavDropdown from './AssetServiceNavDropdown';
import {ExternalAssetAlbum} from '../RemoteAssetService';
import AssetServiceNavSearch from './AssetServiceNavSearch';
import {AssetService} from '@taopix/taopix-core/dist/types/types';
import {useUiState} from '../../Context/UserInterface/UserInterfaceContext';

export type AssetServiceNavBarProps = {
  goHome: Function;
  goBack: Function;
  goUserProfile: Function;
  assetService: AssetService;
  albums: ExternalAssetAlbum[];
  onClickAlbum: Function;
  currentAlbumId: string;
  authenticated: boolean;
  assetServiceLogout: () => void;
  searchFunction?: Function;
  searchMode: boolean;
};

const AssetServiceNavBar = ({
  goHome,
  goBack,
  goUserProfile,
  assetService,
  albums,
  onClickAlbum,
  currentAlbumId,
  authenticated,
  assetServiceLogout,
  searchFunction = () => {},
  searchMode = false,
  ...props
}: AssetServiceNavBarProps) => {
  const {t} = useTranslation();
  const ui = useUiState();

  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);

  const toggleUserMenu = () => {
    setUserMenuOpen(!userMenuOpen);
  };

  const onSearchSubmit = (text: string) => {
    searchFunction(text);
    dropdownNavEnabled = false;
    navEnabled = true;
  };

  const onNavigate = (levels: number) => {
    if (searchRef.current) {
      searchRef.current.value = '';
    }
    goBack(levels);
  };

  let startIcon = <PictureIcon />;
  let logoutLabel = t('*:str_LabelLogout');
  let navEnabled = albums.length > 1;
  let dropdownNavEnabled = navEnabled;

  useEffect(() => {
    switch (assetService.code) {
      case '%INSTAGRAM%':
        startIcon = <InstagramIcon />;
        navEnabled = false;
        dropdownNavEnabled = false;
        break;
      case '%FACEBOOK%':
        startIcon = <FacebookIcon />;
        break;
      case '%GOOGLEPHOTOS%':
        startIcon = <GooglePhotosIcon />;
        logoutLabel = t('assetservices:str_ButtonDisconnectFromGooglePhotos ', {
          ns: 'assetservices',
        });
        break;
      case '%FLICKR%':
        startIcon = <FlickrIcon />;
        break;
      default:
        navEnabled = !(currentAlbumId === 'me');
        dropdownNavEnabled = albums.length > 1;
        break;
    }
  }, [currentAlbumId, searchMode, assetService]);

  return (
    <>
      <div id="nav" className="flex sticky top-0 z-20 justify-between pb-sm">
        {authenticated && (
          <>
            <Button
              disabled={currentAlbumId === '' || !navEnabled}
              buttonStyle={'standard'}
              size={'small'}
              startIcon={<ChevronLeftIcon />}
              label={t('*:str_LabelBack')}
              hideLabel
              onClick={() => {
                onNavigate(1);
              }}
            />
            <AssetServiceNavDropdown
              goBack={onNavigate}
              assetService={assetService}
              albums={albums}
              currentAlbumId={currentAlbumId}
              navEnabled={dropdownNavEnabled}
            />
            <Button
              id={'asset-service-user-menu-button'}
              className="h-full"
              buttonStyle={'standard'}
              size={'small'}
              startIcon={<UserIcon />}
              endIcon={<ChevronDownSmallIcon />}
              label={t('*:str_LabelToggleMenu', {defaultValue: 'Toggle menu'})}
              hideLabel
              onClick={toggleUserMenu}
            />
            <PopOut
              id={'asset-service-user-dropdown'}
              displayMode={ui.state.isSmallScreen ? 'modal' : 'positioned'}
              offset={{x: 0, y: 0}}
              open={userMenuOpen}
              anchorOrigin={{
                horizontal: Horizontal.Right,
                vertical: Vertical.Bottom,
              }}
              transformOrigin={{
                horizontal: Horizontal.Right,
                vertical: Vertical.Top,
              }}
              onClose={toggleUserMenu}
              anchorId={'asset-service-user-menu-button'}
              excludeIdFromClickOutside={'asset-service-user-menu-button'}
              repositionOnResize
              theme={'tpx-theme-dialogs'}
              themeTip={'Dialogs'}
              contentTheme={ThemeName.Popouts}
            >
              <Button
                buttonStyle={'standard'}
                startIcon={startIcon}
                label={logoutLabel}
                corners={'square'}
                onClick={() => assetServiceLogout()}
              />
            </PopOut>
          </>
        )}
      </div>
      {assetService.searchmode && authenticated ? (
        <AssetServiceNavSearch onSubmit={onSearchSubmit} ref={searchRef} />
      ) : null}
    </>
  );
};

export default AssetServiceNavBar;
